import { useDashboardUIActions } from "@contexts/dashboardUI";
import { useProjectActions } from "@contexts/projects";
import { getIndicatorColor } from "@utils/colorCalculations";
import { formatNumberToLocale } from "@utils/formatNumber";
import { useProjectContext } from "@contexts/projects";
import { isBuildingTrial } from "@utils/checkPermiossion";
import AnnulmentIndicator from "@components/titleAnnulments/AnnulmentIndicator";
import {
  combineMetricEnvelopes,
  deriveMetricEnvelope,
  metricEnvelopeFor,
  metricPresentation,
  metricReasonStyle,
} from "@utils/metricPresentation";
import {
  buildValueNodeIdentity,
  useValueHierarchy,
} from "./ValueHierarchyContext";

const { useState, useCallback, useRef } = require("react")

const AccordionItem = ({
  item,
  type,
  typeTitle,
  ChildAccordionComponent,
  filters,
  path = [],
}) => {
  const { toggleItemDetailOffcanvas } = useDashboardUIActions();
  const { activeProject, cubBased } = useProjectContext();
  const { selectDetail } = useProjectActions();
  const { openNode, closeNode } = useValueHierarchy();
  const { first, second, third, fourth } = filters
  const [toggle, setToggle] = useState(false)
  const toggleRef = useRef(null);
  const label = item.description ?? item.building_unit_name ?? "Item sem descrição";
  const wbsCodeId = item.wbs_code_id ?? item.building_unit_id ?? item.wbs;
  const identity = buildValueNodeIdentity(type, wbsCodeId);
  const node = {
    ...identity,
    type,
    wbsCodeId,
    label,
  };
  const nodePath = [...path, node];
  const activateNode = useCallback(() => {
    openNode(nodePath);
  }, [nodePath, openNode]);
  const onShowDetail = useCallback(() => {
    activateNode();
    selectDetail(item, type)
    toggleItemDetailOffcanvas()
  }, [activateNode, item, type])

  const onLeafFocus = useCallback(() => {
    if (!ChildAccordionComponent) activateNode();
  }, [activateNode, ChildAccordionComponent]);

  const closeBranch = useCallback(() => {
    setToggle(false);
    closeNode(node.key);
    toggleRef.current?.focus();
  }, [closeNode, node.key]);

  const toggleBranch = useCallback(() => {
    setToggle((current) => {
      const next = !current;
      if (next) openNode(nodePath);
      else closeNode(node.key);
      return next;
    });
  }, [closeNode, node.key, nodePath, openNode]);

  const onBranchKeyDown = useCallback((event) => {
    if (event.key === "Escape" && toggle) {
      event.preventDefault();
      event.stopPropagation();
      closeBranch();
    }
  }, [closeBranch, toggle]);

  const metricRecord = item.stockValue === undefined && item.stock?.value !== undefined
    ? { ...item, stockValue: item.stock.value }
    : item;
  const rowReasonKey = [type, item.wbs_code_id, item.building_unit_id, item.wbs]
    .filter((part) => part !== undefined && part !== null && part !== "")
    .join("-")
    .replace(/[^a-zA-Z0-9_-]/g, "-");
  const envelopeFor = (metricKey, legacyKey = metricKey) => metricEnvelopeFor(
    metricRecord,
    metricKey,
    legacyKey,
  );
  const present = (envelope, slot, formatter = formatNumberToLocale) => metricPresentation(
    envelope,
    { formatter, reasonId: `metric-accordion-${rowReasonKey}-${slot}-reason` },
  );
  const asDisplayedCurrency = (envelope, { current = false } = {}) => (
    cubBased
      ? deriveMetricEnvelope(
        envelope,
        (value) => {
          const indexer = current
            ? activeProject?.indexer?.value
            : activeProject?.originalIndexer?.value;
          return typeof indexer === "number" && Number.isFinite(indexer) && indexer !== 0
            ? value / indexer
            : null;
        },
        { id: `${envelope?.id || "metric"}_cub`, unit: "CUB" },
      )
      : envelope
  );
  const budgetEnvelope = envelopeFor("budget");
  const budgetColumnEnvelope = first === "budget" || cubBased
    ? budgetEnvelope
    : envelopeFor("indexedBudget", "indexed_budget");
  const budgetPresentation = present(asDisplayedCurrency(budgetColumnEnvelope), "budget");
  const predictedPresentation = present(asDisplayedCurrency(envelopeFor("predicted")), "predicted");
  const measuredEnvelope = envelopeFor("measured");
  const measuredPresentation = present(asDisplayedCurrency(measuredEnvelope), "measured");

  const hasExtraValues = Array.isArray(item.extraValue);
  const extraValues = hasExtraValues ? item.extraValue : [];
  const extraValuesValid = hasExtraValues && extraValues.every(({ value }) => (
    typeof value === "number" && Number.isFinite(value)
  ));
  const extraEnvelope = {
    id: "extraValue",
    value: extraValuesValid ? extraValues.reduce((sum, { value }) => sum + value, 0) : null,
    unit: "BRL",
    status: extraValuesValid ? "ok" : hasExtraValues ? "degraded" : "source_unavailable",
  };

  const metricKeyByField = {
    idc: "idc",
    trend: "trend",
    trendExtra: "trend",
    stockIdc: "stockIdc",
    stockTrend: "stockTrend",
  };
  const thirdMetricKey = metricKeyByField[third];
  const sourceThirdEnvelope = thirdMetricKey
    ? envelopeFor(thirdMetricKey, third)
    : envelopeFor(third);
  const thirdEnvelope = third === "trendExtra"
    ? combineMetricEnvelopes(
      [sourceThirdEnvelope, extraEnvelope],
      ([trendValue, extraValue]) => trendValue + extraValue,
      { id: "trend_extra", unit: "currency" },
    )
    : sourceThirdEnvelope;
  const thirdPresentation = present(thirdEnvelope, `third-${third}`);
  const color = getIndicatorColor(thirdPresentation.value);
  const percentagePresentation = (envelope, slot) => present(
    combineMetricEnvelopes(
      [envelope, budgetEnvelope],
      ([value, budget]) => budget !== 0 ? (value / budget) * 100 : null,
      { id: `${slot}_percentage`, unit: "percentage" },
    ),
    `${slot}-percentage`,
    (value) => String(Math.round(value * 100) / 100),
  );
  const thirdPercentage = percentagePresentation(thirdEnvelope, `third-${third}`);
  const measuredPercentage = percentagePresentation(measuredEnvelope, "measured");

  const paidEnvelope = envelopeFor("paid");
  const stockValueEnvelope = envelopeFor("stockValue");
  const accumulatedEnvelope = envelopeFor("accumulated");
  let fourthEnvelope;
  if (fourth === "paid") {
    fourthEnvelope = cubBased ? envelopeFor("paidCub") : paidEnvelope;
  } else if (fourth === "paidStock") {
    fourthEnvelope = asDisplayedCurrency(combineMetricEnvelopes(
      [paidEnvelope, stockValueEnvelope],
      ([paid, stock]) => paid - stock,
      { id: "paidStock", unit: "BRL" },
    ));
  } else if (fourth === "accumulatedStock") {
    fourthEnvelope = asDisplayedCurrency(combineMetricEnvelopes(
      [accumulatedEnvelope, stockValueEnvelope],
      ([accumulated, stock]) => accumulated - stock,
      { id: "accumulatedStock", unit: "BRL" },
    ));
  } else {
    fourthEnvelope = asDisplayedCurrency(accumulatedEnvelope);
  }
  const fourthPresentation = present(fourthEnvelope, `fourth-${fourth}`);

  let secondEnvelope;
  if (second === "commited") {
    secondEnvelope = cubBased
      ? combineMetricEnvelopes(
        [envelopeFor("paidCub"), envelopeFor("toPayCub")],
        ([paid, toPay]) => paid + toPay,
        { id: "committedCub", unit: "CUB" },
      )
      : envelopeFor("committed", "commited");
  } else if (second === "budget_minus_paid" || second === "indexed_balance") {
    secondEnvelope = cubBased
      ? combineMetricEnvelopes(
        [asDisplayedCurrency(budgetEnvelope), envelopeFor("paidCub")],
        ([budget, paid]) => budget - paid,
        { id: second, unit: "CUB" },
      )
      : envelopeFor(
        second === "budget_minus_paid" ? "budgetMinusPaid" : "indexedBalance",
        second,
      );
  } else if (second === "commited_extra") {
    secondEnvelope = asDisplayedCurrency(combineMetricEnvelopes(
      [envelopeFor("committed", "commited"), extraEnvelope],
      ([committed, extra]) => committed + extra,
      { id: "committedExtra", unit: "BRL" },
    ));
  } else if (second === "extraValue") {
    secondEnvelope = asDisplayedCurrency(extraEnvelope);
  } else if (second === "stockValue") {
    secondEnvelope = asDisplayedCurrency(combineMetricEnvelopes(
      [envelopeFor("apiStockValue"), stockValueEnvelope],
      ([apiStock, ampliStock]) => apiStock + ampliStock,
      { id: "stockValue", unit: "BRL" },
    ));
  } else if (second === "stockValueApi") {
    secondEnvelope = asDisplayedCurrency(envelopeFor("apiStockValue"));
  } else if (second === "stockValueAmpli") {
    secondEnvelope = asDisplayedCurrency(stockValueEnvelope);
  } else {
    secondEnvelope = asDisplayedCurrency(envelopeFor(second));
  }
  const secondPresentation = present(secondEnvelope, `second-${second}`);

  const committedPercentage = percentagePresentation(envelopeFor("committed", "commited"), "committed");
  const paidPercentage = percentagePresentation(paidEnvelope, "paid");
  const accumulatedPercentage = percentagePresentation(accumulatedEnvelope, "accumulated");

  const getField = (field) => {
    let res;
    switch (field) {
      case 'commited':
        res = `Comprometido - ${committedPercentage.text}%`
        break;
      case 'extraValue':
        res = 'Estouro / Economia'
        break;
      case 'commited_extra':
        res = 'Comprometido + Estouros / Economias'
        break;
      case 'budget_minus_paid':
        res = 'Saldo'
        break;
      case 'indexed_balance':
        res = 'Saldo Indexado'
        break;
      case 'accumulated':
        res = `Realizado - ${accumulatedPercentage.text}%`
        break;
      case 'accumulatedStock':
        res = 'Realizado - Estoque'
        break;
      case 'stockValue':
        res = 'Em Estoque'
        break;
      case 'stockValueApi':
        res = 'Em Estoque (Sienge)'
        break;
      case 'stockValueAmpli':
        res = 'Em Estoque (Ampli)'
        break;
      case 'idc':
        res = 'IDC'
        break;
      case 'trend':
        res = `Tendência - ${thirdPercentage?.text ?? "—"}%`
        break;
      case 'stockIdc':
        res = 'IDC com Estoque'
        break;
      case 'stockTrend':
        res = 'Tendência com Estoque'
        break;
      case 'trendExtra':
        res = `Tendência + Estouros / Economias - ${thirdPercentage?.text ?? "—"}%`
        break;
      case 'paid':
        res = `Pago - ${paidPercentage.text}%`
        break;
      case 'paidStock':
        res = 'Pago - Estoque'
        break;

      default:
        break;
    }
    return res
  }

  const checkTrend = () => {
    // if (item[third] > item[first]) return 'red'
    // return 'green'
  }

  const getLevel = () => {
    const shadesOfGray = ['#f8f8f8', '#f0f0f0', '#e8e8e8', '#e0e0e0', '#d8d8d8'];
    let res;
    switch (type) {
      case 'unit':
        res = shadesOfGray[0]
        break;

      case 'cell':
        res = shadesOfGray[1]
        break;

      case 'step':
        res = shadesOfGray[2]
        break;

      case 'substep':
        res = shadesOfGray[3]
        break;

      case 'service':
        res = shadesOfGray[4]
        break;

      default:
        break;
    }
    return { backgroundColor: res }
  }

  // Vermelho do Pago: compara na régua exibida. Em CUB, o Pago datado (cub_value por
  // pagamento) contra o Orçado ÷ índice-base — sobra em CUB é custo real, nunca inflação.
  const checkPaidField = () => {
    if (fourth !== "paid" && fourth !== 'paidStock') return
    if (
      fourthPresentation.available
      && budgetPresentation.available
      && fourthPresentation.value > budgetPresentation.value
    ) return 'color-red'
  }

  const renderValue = (presentation, className = "", indicator = null) => <>
    <span className={`value ${className}`} aria-describedby={presentation.describedBy}>
      {presentation.text}
      {indicator}
    </span>
    {!presentation.available && (
      <span id={presentation.describedBy} style={metricReasonStyle}>{presentation.reason}</span>
    )}
  </>;

  const HeadingTag = {
    unit: "h3",
    cell: "h4",
    step: "h5",
    substep: "h6",
  }[type];
  const title = HeadingTag
    ? <HeadingTag className="accordion-title">{label}</HeadingTag>
    : <span className="accordion-title">{label}</span>;

  return (
    <div
      className="accordion-item"
      id={identity.elementId}
      tabIndex={-1}
      onKeyDown={onBranchKeyDown}
      onFocus={onLeafFocus}
    >
      <div className="accordion-item-body" style={getLevel()}>
        <div>
          <div className="flex align-center gap-8">
            {ChildAccordionComponent && (
              <button
                ref={toggleRef}
                type="button"
                className={`accordion-toggle ${toggle ? "active" : ""}`}
                onClick={toggleBranch}
                aria-label={`${toggle ? "Recolher" : "Expandir"} ${label}`}
                aria-expanded={toggle}
                aria-controls={identity.childrenId}
              />
            )}
            <div className="grid">
              <span className="accordion-label">{typeTitle !== 'Centro de Custo' && `EAP #${item.building_unit_id}${item.wbs ? `.${item.wbs}` : ""} /`} {typeTitle}</span>
              {title}
            </div>
          </div>
        </div>
        <div className="column-span-2-tablet">
          <div className="flex wrap-wrap wrap-nowrap-tablet gap-16 align-center">
            <div className="accordion-content value-accordion">
              <div className="data">
                <span className="label">Orçado {first === 'budget' ? '' : 'Indexado'}</span>
                {/* CUB: nominal ÷ índice-base; valor já indexado a hoje ÷ índice atual (senão a inflação conta 2×) */}
                {renderValue(budgetPresentation)}
              </div>
              {!isBuildingTrial() &&
                <>
                  <div className="data">
                    <span className="label">Previsto</span>
                    {renderValue(predictedPresentation)}
                  </div>
                  <div className="flex gap-8">
                    {/* <CircularBar value={Math.round(+((item.measured / item.budget) * 100))} /> */}
                    <div className="data">
                      <span className="label align-self-end margin-right-16">Medido - {measuredPercentage.text}%</span>
                      {renderValue(measuredPresentation)}
                    </div>
                  </div>
                  <div className="data">
                    <span className="label">{getField(fourth)}</span>
                    {renderValue(
                      fourthPresentation,
                      checkPaidField(),
                      // Pago do item: títulos anulados apropriados neste nó da EAP ou abaixo dele.
                      (fourth === "paid" || fourth === "paidStock")
                        ? <AnnulmentIndicator summary={activeProject?.titleAnnulments} kind="paid" wbsCode={item.wbs_code_id ?? ""} />
                        : null,
                    )}
                  </div>
                  <div className="data">
                    <span className="label">{getField(second)}</span>
                    {renderValue(
                      secondPresentation,
                      secondPresentation.available && secondPresentation.value < 0 ? 'color-red' : '',
                    )}
                  </div>
                  <div className="data">
                    <span className="label">{getField(third)}</span>
                    <span
                      className={`value ${third.toLowerCase().includes('idc') && thirdPresentation.available ? `color-${color}` : ''} ${third.toLowerCase().includes('trend') ? `color-${checkTrend() || ""}` : ''}`}
                      aria-describedby={thirdPresentation.describedBy}
                    >
                      {thirdPresentation.text}
                    </span>
                    {!thirdPresentation.available && (
                      <span id={thirdPresentation.describedBy} style={metricReasonStyle}>
                        {thirdPresentation.reason}
                      </span>
                    )}
                  </div>
                </>
              }
            </div>
            <div className="accordion-actions">
              <button
                type="button"
                className="toggle-offcanvas"
                onClick={onShowDetail}
                aria-label={`Ver detalhes de ${label}`}
              >
                <i
                  className="icon icon-width-16 icon-ellipsis-h"
                  aria-hidden={true}
                ></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      {ChildAccordionComponent && (
        <div
          id={identity.childrenId}
          className="value-hierarchy-children"
          hidden={!toggle}
          tabIndex={-1}
        >
          {toggle && (
            <ChildAccordionComponent
              parent={item.wbs_code_id}
              parentLabel={label}
              filters={filters}
              path={nodePath}
              containerId={identity.childrenId}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default AccordionItem;
